import '../Main.scss';

import * as React from 'react';

import {
    Button,
    Col,
    Container,
    Modal,
    ModalBody,
    Row
} from 'reactstrap';

import { Theme, ThemeProvider } from '@mui/material/styles';
import { CreateStdTheme } from '../../Utils/Style';

import DismissIcon from '@mui/icons-material/Close';

import { BrandConfig } from '../../Store/Tenant';
import { PortalCreateSubmissionRequestOptions } from '../../Models/PortalCreateSubmissionRequestOptions';

import { FormatDate, formatStringMDYFullMonth } from '../../Utils/DateUtils';
import { StringFormat } from '../../Utils/StringUtils';

import { AccountingPkgPresentation } from '../SelectAccountingSystem/AccountingPkgPresentation';
import { AttributedParagraphSet } from '../../Utils/AttributedParagraph';

import { PortalTextBorrowerParameters } from '../../Models/Api/strongbox.financialportal';
import { pgIconDescriptors } from './BorrowerParametersContainer';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faShieldCheck,
    faQuestion,
    faLock,
    faEye,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';

type Props = {
    dismissModal: (cancelled: boolean) => void;
    lenderName: string;
    parameters: PortalCreateSubmissionRequestOptions;
    brandConfig: BrandConfig;
    reportsList: string[];
    accountingPkg?: AccountingPkgPresentation;
    securityBullets: AttributedParagraphSet;
    borrowerParametersText?: PortalTextBorrowerParameters;
};

type BulletPoint = {
    bullet: string;
    bulletParagraphs: string[];
    iconDescriptor: string;
};

export const BorrowerParametersModal: React.FC<Props> = (props): React.ReactElement => {
    const {
        accountingPkg,
        securityBullets,
        borrowerParametersText,
    } = props;

    const idContinueButton = 'continueWithAcccountingSystemButton'

    const [muiTheme] = React.useState<Theme>(() => {
        return CreateStdTheme(props.brandConfig);
    });

    const [setInitialFocus, setSetInitialFocus] = React.useState<boolean>(false);
    
    React.useEffect(() => {
        setSetInitialFocus(true);

        // I want this to execute equivalent to componentDidMount so this is appropriate
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // As this is a modal and the page isn't 'loading' unfortunately the autofocus attribute
    // on the button on its own doesn't appear to work.

    React.useEffect(() => {
        if (setInitialFocus) {
            const continueButton = document.getElementById(idContinueButton);
            if (continueButton !== null) {
                continueButton.focus();
            }
            setSetInitialFocus(false);
        }
    }, [setInitialFocus]);

    const securityBulletContent = React.useMemo(() => {
        const result: BulletPoint[] = [];
        let currentBullet: BulletPoint | undefined = undefined;

        securityBullets.paragraphs.forEach(pg => {
            if (pg.attributes.find(attr => attr.name === 'bullet')) {
                const iconDescriptor = pg.attributes.find(attr => attr.name === 'iconDescriptor');
                if (!!currentBullet) {
                    result.push(currentBullet);
                }
                currentBullet = {
                    bullet: pg.content,
                    bulletParagraphs: [],
                    iconDescriptor: !!iconDescriptor ? iconDescriptor.value : '',
                }
            } else {
                if (!!currentBullet) {
                    let bulletContent = pg.content;

                    if (pg.attributes.find(attr => attr.name === 'replaceLenderName')) {
                        // replacing lenderName at runtime.  Not actually doing templated string here but I liked keeping a consistent mechanism in place.
                        // eslint-disable-next-line no-template-curly-in-string
                        bulletContent = bulletContent.replace('${lenderName}', props.lenderName);
                    }
                    if (pg.attributes.find(attr => attr.name === 'anonymizedOnly')) {
                        if (props.parameters.anonymizeCustomersAndVendors) {
                            currentBullet.bulletParagraphs.push(bulletContent);
                        }
                    } else {
                        currentBullet.bulletParagraphs.push(bulletContent);
                    }
                }
            }
        });

        if (!!currentBullet) {
            result.push(currentBullet);
        }

        return result;
        // This is the actual dependency list I want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [securityBullets]);

    const getCollectionIntervalText = (): string => {
        let dateText;
        let dateIsCurrentYear = true;

        // Check against undefined.  Because months start at 0, it can be valid but still fail on 'if (month)'.
        if ((props.parameters.mostRecentPeriodMonth !== undefined) &&
            (props.parameters.mostRecentPeriodYear !== undefined) &&
            (props.parameters.mostRecentPeriodDay !== undefined)) {
            const date = new Date(props.parameters.mostRecentPeriodYear, props.parameters.mostRecentPeriodMonth, props.parameters.mostRecentPeriodDay);
            dateText = FormatDate(date, formatStringMDYFullMonth);
            if (new Date(Date.now()).getFullYear() !== props.parameters.mostRecentPeriodYear) {
                dateIsCurrentYear = false;
            }
        }

        let result;

        if (props.parameters.fullFiscalYearsToCollect === 0) {
            if (!!dateText) {
                if (dateIsCurrentYear) {
                    result = StringFormat(borrowerParametersText?.templateRequestForCurrentFYThrough || '{0}', dateText);
                } else {
                    result = StringFormat(borrowerParametersText?.templateRequestForFYThrough || '{0}', dateText);
                }
            } else {
                result = borrowerParametersText?.requestForCurrentFY || '';
            }
        } else if (!!dateText) {
            if (dateIsCurrentYear) {
                if (props.parameters.fullFiscalYearsToCollect === 1) {
                    result = StringFormat(borrowerParametersText?.templateCurrentFYAndPriorYear || '{0}', dateText);
                } else {
                    result = StringFormat(borrowerParametersText?.templateCurrentFYAndPriorYears || '{0}{1}', dateText, props.parameters.fullFiscalYearsToCollect.toString());
                }
            } else {
                if (props.parameters.fullFiscalYearsToCollect === 1) {
                    result = StringFormat(borrowerParametersText?.templateFYAndPriorYear || '{0}', dateText);
                } else {
                    result = StringFormat(borrowerParametersText?.templateFYAndPriorYears || '{0}{1}', dateText, props.parameters.fullFiscalYearsToCollect.toString());
                }
            }
        } else {
            if (props.parameters.fullFiscalYearsToCollect === 1) {
                result = borrowerParametersText?.lastFiscalYear || '';
            } else {
                result = StringFormat(borrowerParametersText?.templateLastFiscalYears || '{0}', props.parameters.fullFiscalYearsToCollect.toString());
            }
        }

        return result;
    }

    const checkColStyle = {
        paddingRight: '0px',
        paddingTop: '2px'
    };

    const continueButtonContent = React.useMemo(() => {
        return !!accountingPkg ? StringFormat(borrowerParametersText?.templateContinueWithAccountingPackage || '{0}', accountingPkg.friendlyName) : ''

        // These are the only dependencies I want.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountingPkg]);

    const youWillBePromptedText = React.useMemo(() => {
        return !!accountingPkg ? StringFormat(borrowerParametersText?.templateYouWillBePrompted || '{0}', accountingPkg.friendlyName) : '';
    }, [borrowerParametersText, accountingPkg]);

    const iconFromDescriptor = (descriptor: string) => {
        switch (descriptor) {
            case pgIconDescriptors.private:
                return faLock;
            case pgIconDescriptors.readonly:
                return faEye;
            case pgIconDescriptors.secure:
                return faShieldCheck;
        }
        return faQuestion;
    }

    return (
        <Modal
            className={`flight-modal`}
            style={{
                width: '75%',

            }}
            isOpen={true}
            backdrop={'static'}
            toggle={() => props.dismissModal(true)}
        >
            <ModalBody className={'unpadded'}>
                <ThemeProvider theme={muiTheme}>
                    <Container fluid className={`control-region control-region-borrower`}>
                        <Row>
                            <Col xs={4} className={'left-background-pane'}>
                                {securityBulletContent.map((bullet, i) => {
                                    return (
                                        <Row
                                            className={'bullet-row'}
                                            key={`security-bullet-row-${i.toString()}`}
                                        >
                                            <Col xs={'auto'} style={checkColStyle}>
                                                <FontAwesomeIcon
                                                    icon={iconFromDescriptor(bullet.iconDescriptor)}
                                                />
                                            </Col>
                                            <Col>
                                                <Row><span className={'heavy'}>{bullet.bullet}</span></Row>
                                                {
                                                    bullet.bulletParagraphs.map((pg, pgi) => {
                                                        return (
                                                            <Row key={`security-bullet-row-text-${i.toString()}-${pgi.toString()}`}>
                                                                <span className={'hide-for-small-display'}>{pg}</span>
                                                            </Row>
                                                        );
                                                    })
                                                }
                                            </Col>
                                        </Row>
                                    )
                                })}
                            </Col>
                            <Col className={'right-normal-pane'}>
                                <Row className={'spaced-row'}>
                                    <Col>
                                        <h1>{borrowerParametersText?.collectionRequest || ''}</h1>
                                    </Col>
                                    <Col 
                                        xs={1}
                                        style={{
                                            padding: '0px',
                                        }}
                                    >
                                        <Button
                                            className={'simple-icon-button small-scaled-button'}
                                            onClick={() => props.dismissModal(true)}
                                            name={borrowerParametersText?.cancelImport || ''}
                                            tabIndex={2}
                                        >
                                            <DismissIcon />
                                        </Button>
                                    </Col>
                                </Row>
                                <Row className={'bordered-region std-vertical-content-separator'}>
                                    <Col>
                                        <Row>
                                            <Col>
                                                <h2>{borrowerParametersText?.collectionPeriod || ''}</h2>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <ul className={'small-bullets'}>
                                                    <li>{getCollectionIntervalText()}</li>
                                                </ul>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <h2 className={'extra-small-header-separator'}>
                                                    {borrowerParametersText?.reportsRequested || ''}
                                                </h2>
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <ul className={'small-bullets'}>
                                                    {
                                                        props.reportsList.map((report, index) => {
                                                            return (
                                                                <li key={`reportList${index}`}>{report}</li>
                                                            )
                                                        })
                                                    }
                                                </ul>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col 
                                        className={'col-centered std-top-margin-vertical-content-separator'}
                                    >
                                        <p>{youWillBePromptedText}</p>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className={'button-row-centered std-vertical-content-separator'}>
                                        <Button
                                            color="unset"
                                            className={'modal-button'}
                                            onClick={() => props.dismissModal(false)}
                                            tabIndex={1}
                                            name={continueButtonContent}
                                            id={idContinueButton}
                                        >
                                            {continueButtonContent}
                                        </Button>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    </Container>
                </ThemeProvider>
            </ModalBody>
        </Modal>
    );
}
